import React, { useEffect, useContext, useRef, useState } from "react";
import ReactGA from "react-ga4";
import {
  HashRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { PropagateLoader } from "react-spinners";

// Component Imports
import Dashboard from "./components/dashboard/Dashboard";
import SalesRepDashboard from "./components/salesRepDashboard/SalesRepDashboard";
import Register from "./components/register/Register";
import Login from "./components/login/Login";
import Navbar from "./components/navbar/Navbar";
import About from "./components/about/About";
import Products from "./components/products/Products";
import Contact from "./components/contact/Contact";
import MFA from "./components/MFA/MFA";
import Home from "./components/home/Home";
import Footer from "./components/footer/Footer";
import ProviderProfileCard from "./components/profile/ProviderProfileCard";
import VerifyEmail from "./components/verifyEmail/VerifyEmail";
import ForgotPassword from "./components/login/ForgotPassword";
import ResetPassword from "./components/login/ResetPassword";
import BAAAgreementPage from "./components/login/BAAAgreementPage";
import TreatmentSelection from "./components/treatment/TreatmentSelection";
import SalesRepPatientDetail from "./components/dashboard/salesrep/SalesRepPatientDetail";
import SalesRepProviderPatients from "./components/dashboard/salesrep/SalesRepProviderPatients";
import SalesRepOrderDetail from "./components/dashboard/salesrep/SalesRepOrderDetail";
import HowItWorks from "./components/howItWorks/HowItWorks";
import PrivacyPolicy from "./components/privacy/PrivacyPolicy";
import TermsAndConditions from "./components/terms/TermsAndConditions";
import OrderForm from "./components/orderForm/OrderForm";
// Utility Imports
// 🔑 AuthProvider is imported correctly here
import { AuthContext, AuthProvider } from "./utils/context/auth";

import "./App.css";

// ScrollToTop component to scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Helper function for route classification
const isPublicRoute = (path) =>
  path === "/" ||
  path === "/register" ||
  path === "/about" ||
  path === "/products" ||
  path === "/contact" ||
  path.startsWith("/verify-email") ||
  path.startsWith("/reset-password") ||
  path === "/forgot-password";

const isAuthRoute = (path) =>
  path === "/login" || path === "/mfa" || path === "/baa-agreement";
const isSecuredRoute = (path) =>
  path === "/dashboard" ||
  path === "/sales-rep/dashboard" ||
  path === "/profile" || 
  path === "/order-form";

function AppWrapper() {
  const location = useLocation();
  
  // Track page views on route change
  useEffect(() => {
    ReactGA.send("pageview", {
      page_path: location.pathname + location.search,
    });
  }, [location]);

  // 🔑 CHANGE 1: Destructure isBAARequired from context
  const {
    logout,
    user,
    loading: authLoading,
    isMfaPending,
    isBAARequired, // <-- ADDED THIS CRITICAL STATE
  } = useContext(AuthContext);

  const warningTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const hiddenPaths = [
    "/login",
    "/register",
    "/mfa",
    "/forgot-password",
    "/reset-password",
    "/verify-email",
    "/baa-agreement", // Added BAA screen to hide nav/footer
    "/treatment-selection"
  ];
  const shouldHideNavAndFooter = hiddenPaths.some((path) =>
    location.pathname.startsWith(path)
  );

  // 1. Light Mode Only - Always remove dark class
  useEffect(() => {
    document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", "false");
  }, []);

  // 2. Session Timeout Logic (remains the same)
  useEffect(() => {
    if (!user) return;

    const HIPAA_IDLE_TIMEOUT_MINUTES = 15;
    const WARNING_BEFORE_LOGOUT_SECONDS = 60;

    const warningDuration =
      1000 * 60 * HIPAA_IDLE_TIMEOUT_MINUTES -
      1000 * WARNING_BEFORE_LOGOUT_SECONDS;
    const logoutDuration = 1000 * 60 * HIPAA_IDLE_TIMEOUT_MINUTES;

    const logoutAndRedirect = () => {
      logout();
      toast.error("Logged out due to inactivity.", { id: "logout-toast" });
      setTimeout(() => {
        window.location.replace("/login");
      }, 0);
    };

    const showWarning = () => {
      toast("You will be logged out in 1 minute due to inactivity.", {
        icon: "⚠️",
        duration: WARNING_BEFORE_LOGOUT_SECONDS * 1000,
        id: "logout-toast",
      });
    };

    const resetTimers = () => {
      clearTimeout(warningTimeoutRef.current);
      clearTimeout(logoutTimeoutRef.current);
      toast.dismiss("logout-toast");

      warningTimeoutRef.current = setTimeout(showWarning, warningDuration);
      logoutTimeoutRef.current = setTimeout(logoutAndRedirect, logoutDuration);
    };

    resetTimers();

    const activityEvents = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];

    activityEvents.forEach((event) =>
      window.addEventListener(event, resetTimers)
    );

    return () => {
      clearTimeout(warningTimeoutRef.current);
      clearTimeout(logoutTimeoutRef.current);
      activityEvents.forEach((event) =>
        window.removeEventListener(event, resetTimers)
      );
    };
  }, [logout, user]);

  // 3. Global Auth Loading Check
  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <PropagateLoader
          color={isDarkMode ? "#00A389" : "#10B981"}
          loading={true}
          size={15}
        />
      </div>
    );
  }

  // 🔑 4. CRITICAL ROUTING GUARD LOGIC

  // 🛑 Rule A: BAA must be signed. This is the highest priority lock.
  if (isBAARequired && location.pathname !== "/baa-agreement") {
    return <Navigate to="/baa-agreement" replace />;
  }

  // 🔒 Rule B: Pending MFA must be on the MFA screen.
  if (isMfaPending && location.pathname !== "/mfa") {
    return <Navigate to="/mfa" replace />;
  }

  // Rule C: Logged-in users WITHOUT pending states can access secured routes
  // ✅ CRITICAL FIX: Add checks for !isBAARequired && !isMfaPending
  if (user && !isBAARequired && !isMfaPending) {
    if (isAuthRoute(location.pathname) || isPublicRoute(location.pathname)) {
      const target = user?.user?.email === "ijalab1808@gmail.com" ? "/order-form" : "/dashboard"; // ijalab1808@gmail.com - Katrina's profile
      return <Navigate to={target} replace />;
    }
  }

  // Rule D: Unauthenticated users are blocked from secured routes.
  if (!user && !isMfaPending && !isBAARequired) {
    if (isSecuredRoute(location.pathname)) {
      return <Navigate to="/login" replace />;
    }
  }

  return (
    <>
      <Toaster />
      <ScrollToTop />
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col min-h-screen transition-colors duration-300">
        {/* Navbar */}
        {!shouldHideNavAndFooter && (
          <Navbar isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
        )}

        <main className="flex-grow" style={!shouldHideNavAndFooter ? { paddingTop: location.pathname === "/" ? "35px" : "64px" } : undefined}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/about" element={<About />} />
            <Route path="/products" element={<Products />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
          

            {/* Auth/Lock Routes - Must be outside the secured routes check */}
            <Route path="/baa-agreement" element={<BAAAgreementPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/mfa" element={<MFA />} />

            {/* Private Routes (Secured) */}
            <Route path="/treatment-selection" element={<TreatmentSelection />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/sales-rep/dashboard"
              element={<SalesRepDashboard />}
            />
            <Route path="/order-form" element={<OrderForm />} />
            <Route path="/profile" element={<ProviderProfileCard />} />
            <Route path="/sales-rep/providers/:providerId/patients" element={<SalesRepProviderPatients />} />
            <Route path="/sales-rep/patients/:patientId" element={<SalesRepPatientDetail />} />
            <Route path="/sales-rep/orders/:orderId" element={<SalesRepOrderDetail />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
          </Routes>
        </main>

        {/* Footer */}
        {!shouldHideNavAndFooter && <Footer />}
      </div>
    </>
  );
}

function App() {
  return (
    <HashRouter>
      {/* The AuthProvider must wrap the router and the AppWrapper */}
      <AuthProvider>
        <AppWrapper />
      </AuthProvider>
    </HashRouter>
  );
}

export default App;
